import { ApolloLink, Observable } from "@apollo/client";

const DEFAULT_BATCH_INTERVAL_MS = 100;

type IncrementalChunk = {
  hasNext: boolean;
  incremental?: unknown[];
  pending?: unknown[];
  completed?: unknown[];
  data?: Record<string, unknown> | null;
  extensions?: Record<string, unknown>;
};

/**
 * Only subsequent incremental patches should be buffered. The initial payload
 * (`data` / `pending`) must pass through immediately — delaying it risks an
 * empty UI if the multipart stream is aborted before the batch timer fires.
 */
function shouldBufferChunk(
  result: ApolloLink.Result
): result is ApolloLink.Result & IncrementalChunk {
  if (!("hasNext" in result)) return false;
  // Initial payloads always include `data` — never delay first paint.
  if ("data" in result) return false;
  if (
    "pending" in result &&
    Array.isArray(result.pending) &&
    result.pending.length > 0
  ) {
    return false;
  }
  if (!("incremental" in result)) return false;
  const { incremental } = result;
  return Array.isArray(incremental) && incremental.length > 0;
}

function mergeBufferedChunks(
  chunks: (ApolloLink.Result & IncrementalChunk)[]
): (ApolloLink.Result & IncrementalChunk) | undefined {
  const [last] = chunks.slice(-1);
  if (!last) return undefined;

  const firstWithData = chunks.find(
    (chunk) => "data" in chunk && chunk.data !== undefined
  );
  const firstWithPending = chunks.find(
    (chunk) =>
      "pending" in chunk &&
      Array.isArray(chunk.pending) &&
      chunk.pending.length > 0
  );
  const completed = chunks.flatMap((chunk) =>
    "completed" in chunk && Array.isArray(chunk.completed)
      ? chunk.completed
      : []
  );

  const extensions = chunks.reduce<Record<string, unknown>>((acc, chunk) => {
    if (chunk.extensions) {
      for (const [key, value] of Object.entries(chunk.extensions)) {
        acc[key] = value;
      }
    }
    return acc;
  }, {});

  const merged: ApolloLink.Result & IncrementalChunk = {
    ...last,
    hasNext: last.hasNext,
    incremental: chunks.flatMap((chunk) => chunk.incremental ?? []),
  };

  if (firstWithData !== undefined && "data" in firstWithData) {
    merged.data = firstWithData.data;
  }
  if (firstWithPending !== undefined && "pending" in firstWithPending) {
    merged.pending = firstWithPending.pending;
  }
  if (completed.length > 0) {
    merged.completed = completed;
  }
  if (Object.keys(extensions).length > 0) {
    merged.extensions = extensions;
  }

  return merged;
}

class BatchIncrementalLink extends ApolloLink {
  constructor(batchIntervalMs: number = DEFAULT_BATCH_INTERVAL_MS) {
    super((operation, forward) => {
      const upstream = forward(operation);

      return new Observable<ApolloLink.Result>((subscriber) => {
        let buffer: (ApolloLink.Result & IncrementalChunk)[] = [];
        let timer: ReturnType<typeof setTimeout> | null = null;
        let closed = false;

        const clearTimer = () => {
          if (timer !== null) {
            clearTimeout(timer);
            timer = null;
          }
        };

        const flush = () => {
          clearTimer();
          if (buffer.length === 0 || closed) return;

          const chunks = buffer;
          buffer = [];

          const merged = mergeBufferedChunks(chunks);
          if (merged === undefined) return;

          subscriber.next(merged);
        };

        const subscription = upstream.subscribe({
          next: (result) => {
            if (closed) return;

            if (!shouldBufferChunk(result)) {
              flush();
              subscriber.next(result);
              return;
            }

            buffer.push(result);

            if (!result.hasNext) {
              flush();
            } else {
              timer ??= setTimeout(flush, batchIntervalMs);
            }
          },
          error: (error: unknown) => {
            if (closed) return;
            flush();
            subscriber.error(error);
          },
          complete: () => {
            if (closed) return;
            flush();
            subscriber.complete();
          },
        });

        return () => {
          closed = true;
          // Drop buffered follow-up patches — do not emit during teardown.
          // Emitting here re-enters Apollo while the multipart fetch is being
          // aborted and surfaces as an uncaught AbortError with empty results.
          buffer = [];
          clearTimer();
          subscription.unsubscribe();
        };
      });
    });
  }
}

export { BatchIncrementalLink, mergeBufferedChunks, shouldBufferChunk };
